import { PropertiesStore } from '@hortplus/properties-react';
import { Formik } from 'formik';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { Button, Modal } from 'react-bootstrap';
import LoadingOverlay from '../LoadingOverlay';
import { useStores } from '../stores/useStores';
import { dateFormatRegex } from '../utils';
import TrapMonitoringRecordsForm from './TrapMonitoringRecordsForm';

function EditTrapMonitoringRecords(props) {
  const { traplogStore } = useStores();

  const databaseYear = traplogStore.selectedDatabaseYear() - 1;

  const hide = () => props.setEditingTrappingResult(null);

  return (
    <Modal
      backdrop='static'
      show={!!props.editingTrappingResult}
      onHide={() => hide()}
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Monitoring Records</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={{
            visited_date: props.editingTrappingResult.date,

            // If there are more than one operators then concatenate unique values
            operator: props.editingTrappingResult.trap_monitoring_records
              .filter(
                (record, index, arr) =>
                  arr.findIndex(
                    (record2) => record2.operator === record.operator
                  ) === index
              )
              .reduce(
                (acc, cur) => (acc === '' ? acc : `${acc}, `) + cur.operator,
                ''
              ),

            trapMonitoringRecords: props.traps.map((trap) => {
              const trapMonitoringRecord =
                props.editingTrappingResult.trap_monitoring_records.find(
                  (record) => record.trap.id === trap.id
                );
              return trapMonitoringRecord
                ? {
                    id: trapMonitoringRecord.id,
                    amount: trapMonitoringRecord.amount,
                    lure_changed: trapMonitoringRecord.lure_changed,
                    base_changed: trapMonitoringRecord.base_changed,
                    comments: trapMonitoringRecord.comments
                      ? trapMonitoringRecord.comments
                      : '',
                    visited_at: trapMonitoringRecord.visited_at,
                    location: trapMonitoringRecord.location
                      ? {
                          latitude:
                            trapMonitoringRecord.location.coordinates[0],
                          longitude:
                            trapMonitoringRecord.location.coordinates[1]
                        }
                      : null,
                    location_accuracy: trapMonitoringRecord.location_accuracy,
                    trap: trapMonitoringRecord.trap
                  }
                : {
                    trap: trap,
                    trap_id: trap.id,
                    amount: 0,
                    lure_changed: false,
                    base_changed: false,
                    comments: '',
                    visited_at: '',
                    location: '',
                    location_accuracy: ''
                  };
            })
          }}
          validate={(values, props) => {
            const errors = {};
            const visitedYear = moment(values.visited_date).year();

            if (!values.visited_date) errors.visited_date = 'Required';
            else if (visitedYear < databaseYear) {
              errors.visited_date = `The year must be ${databaseYear} or later.`;
            } else if (visitedYear > databaseYear + 1) {
              errors.visited_date = `The year cannot be larger than ${
                databaseYear + 1
              }.`;
            } else if (!dateFormatRegex.test(values.visited_date)) {
              errors.visited_date =
                'Invalid date. Please use the format "YYYY-MM-DD", and be careful of leap years.';
            }
            if (!values.operator) errors.operator = 'Required';

            const trapMonitoringRecords = [];
            values.trapMonitoringRecords.forEach((record) => {
              const recordErrors = {};

              if (record.amount !== 0 && !record.amount)
                recordErrors.amount = 'Required';

              if (Object.keys(recordErrors).length > 0)
                trapMonitoringRecords.push(recordErrors);
            });

            if (Object.keys(trapMonitoringRecords).length > 0)
              errors.trapMonitoringRecords = trapMonitoringRecords;

            return errors;
          }}
          onSubmit={(values, actions) => {
            const body = {
              records: values.trapMonitoringRecords.map((record) => {
                return {
                  ...record,
                  visited_date: values.visited_date,
                  operator: values.operator
                };
              })
            };

            fetch(
              `${process.env.REACT_APP_TRAPLOG_API_URL}api/trap-monitoring-records`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  Authorization: `Bearer ${traplogStore.bearerToken}`,
                  SeasonalDatabase: traplogStore.selectedDatabase
                },
                body: JSON.stringify(body)
              }
            )
              .then((res) => {
                if (res.ok) return res.json();
                else if (res.status === 401) traplogStore.refreshBearerToken();
                else throw Error(res.statusText);
              })
              .then((data) => {
                traplogStore.loadTrappingZones(
                  traplogStore.bearerToken,
                  traplogStore.refreshBearerToken,
                  traplogStore.selectedDatabase,
                  props.propertiesStore.selectedPropertyId
                );
                hide();
              })
              .catch((err) => {
                if (process.env.NODE_ENV === 'development') console.error(err);
              })
              .finally(() => {
                actions.setSubmitting(false);
              });
          }}
        >
          {(props) => (
            <Fragment>
              <TrapMonitoringRecordsForm {...props} />
              <Button
                varient='primary'
                type='button'
                onClick={props.handleSubmit}
              >
                Submit
              </Button>
              {props.isSubmitting ? <LoadingOverlay /> : null}
            </Fragment>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

EditTrapMonitoringRecords.propTypes = {
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,
  editingTrappingResult: PropTypes.object.isRequired,
  setEditingTrappingResult: PropTypes.func.isRequired,
  traps: PropTypes.array.isRequired
};

export default observer(EditTrapMonitoringRecords);
